/**
 * useSyncStatus Hook
 *
 * Provides real-time synchronization status from the SyncManager.
 * Use this to show sync progress indicators and pending changes count.
 */

import { useState, useEffect, useCallback } from 'react';
import { syncManager, SyncProgress, SyncStatus } from '../services/offline';

/**
 * Hook to track sync status and progress
 */
export function useSyncStatus(): SyncProgress & {
  triggerSync: () => Promise<void>;
  clearQueue: () => Promise<void>;
} {
  const [progress, setProgress] = useState<SyncProgress>(syncManager.getStatus());

  useEffect(() => {
    // Subscribe to sync manager updates
    const unsubscribe = syncManager.subscribe(setProgress);
    return unsubscribe;
  }, []);

  const triggerSync = useCallback(async () => {
    await syncManager.sync();
  }, []);

  const clearQueue = useCallback(async () => {
    await syncManager.clearAll();
  }, []);

  return {
    ...progress,
    triggerSync,
    clearQueue,
  };
}

/**
 * Hook to check if there are pending sync items
 */
export function useHasPendingSync(): boolean {
  const { total, completed } = useSyncStatus();
  return total > completed;
}

/**
 * Hook to get sync status badge info
 */
export function useSyncBadge(): {
  show: boolean;
  count: number;
  variant: 'syncing' | 'pending' | 'error' | 'success';
} {
  const { status, total, completed, failed } = useSyncStatus();

  if (status === 'syncing') {
    return {
      show: true,
      count: total - completed,
      variant: 'syncing',
    };
  }

  if (status === 'error' || failed > 0) {
    return {
      show: true,
      count: failed,
      variant: 'error',
    };
  }

  const pending = total - completed - failed;
  if (pending > 0) {
    return {
      show: true,
      count: pending,
      variant: 'pending',
    };
  }

  return {
    show: false,
    count: 0,
    variant: 'success',
  };
}

/**
 * Hook for sync status text
 */
export function useSyncStatusText(): string {
  const { status, total, completed, failed, currentItem } = useSyncStatus();

  switch (status) {
    case 'syncing':
      return currentItem
        ? `Syncing ${completed + 1}/${total}: ${currentItem}`
        : `Syncing ${completed}/${total}...`;
    case 'offline':
      return 'You are offline. Changes will sync when connected.';
    case 'error':
      return `Sync completed with ${failed} error(s)`;
    case 'idle':
    default:
      return total > 0 ? 'All changes synced' : '';
  }
}

export type { SyncProgress, SyncStatus };
